import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { fetchAuthAccessContext } from '@/auth/accessContext';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useIsAuthenticated } from '@/shared/hooks/auth';
import { queryKeys } from '@/shared/hooks/queryKeys';
import { coreWakeReconnectQueryOptions } from '@/shared/hooks/queryLifecyclePolicies';
import type { OrganisationRole } from '@flowqa/shared';

export const CURRENT_ORG_ROLE_INVALIDATED_EVENT = 'flowqa-current-org-role-invalidated';

export const useCurrentOrgRole = (
  userId: string | null | undefined,
  options?: { enabled?: boolean; organisationId?: string | null },
): {
  role: OrganisationRole | null;
  isAdmin: boolean;
  isLoading: boolean;
  isSuperuser: boolean;
} => {
  const isAuthenticated = useIsAuthenticated();
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  const organisationId = options?.organisationId ?? selectedOrganisationId ?? null;
  const query = useQuery({
    ...coreWakeReconnectQueryOptions,
    queryKey: queryKeys.currentUserRole(userId, organisationId),
    enabled: isAuthenticated && Boolean(userId && organisationId) && (options?.enabled ?? true),
    queryFn: async () => await fetchAuthAccessContext(organisationId),
    staleTime: 0,
  });

  useEffect(() => {
    const handleOrgRemoved = (): void => {
      queryClient.removeQueries({ queryKey: queryKeys.currentUserRolePrefix() });
    };
    const handlePrivilegeInvalidated = (): void => {
      queryClient.removeQueries({ queryKey: queryKeys.currentUserRolePrefix() });
    };

    window.addEventListener('flowqa-org-removed', handleOrgRemoved);
    window.addEventListener(CURRENT_ORG_ROLE_INVALIDATED_EVENT, handlePrivilegeInvalidated);
    return () => {
      window.removeEventListener('flowqa-org-removed', handleOrgRemoved);
      window.removeEventListener(CURRENT_ORG_ROLE_INVALIDATED_EVENT, handlePrivilegeInvalidated);
    };
  }, [queryClient]);

  const isFetchingRole = query.isFetching;
  const role: OrganisationRole | null = isFetchingRole ? null : (query.data?.role ?? null);
  const isSuperuser = isFetchingRole ? false : query.data?.isSuperuser === true;

  return {
    role,
    isAdmin: role === 'ADMIN' || isSuperuser,
    isLoading: query.isLoading || isFetchingRole,
    isSuperuser,
  };
};
